import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const trainingFaqs = [
  {
    q: 'Where are training sessions held?',
    a: 'All in-person sessions are held at courts throughout the greater Milwaukee area. Exact venue details are shared once your session is confirmed through the booking system.',
  },
  {
    q: 'How can I view the schedule and reserve a spot?',
    a: 'Availability, session types, and open time slots are all visible through the booking page. Tap "Book Session" on any service to view the live schedule and reserve.',
  },
  {
    q: 'Why are the 4-session and 8-session packages monthly subscriptions?',
    a: 'The monthly structure keeps players training consistently — which is where real skill development actually happens. It also locks in a lower per-session rate versus paying single-session pricing.',
  },
  {
    q: 'How do I track my remaining sessions, and when do they expire?',
    a: 'You are welcome to track your remaining sessions on your own, but The Dillon Blueprint also uses an automated system to track your session balance for you. You will receive a reminder when your package is running low. The 4-session and 8-session monthly packages expire one month after purchase, and unused sessions do not roll over.',
  },
  {
    q: 'What is the cancellation and rescheduling policy?',
    a: 'Sessions can be rescheduled with at least 24 hours notice at no cost. Cancellations inside that window may forfeit the session — reach out as early as possible and we\'ll do our best to move things around.',
  },
  {
    q: 'How do I cancel my monthly subscription?',
    a: 'Monthly subscriptions can be cancelled at any time. Once cancelled, you keep access to any remaining sessions in your current billing cycle, and you will not be charged going forward.',
  },
  {
    q: 'Do you offer sibling or family training options?',
    a: 'Yes. Siblings and family members can train together in shared sessions or small group setups. Reach out through the contact form for custom family pricing.',
  },
];

const mentorshipFaqs = [
  {
    q: "What if I can't train in person?",
    a: 'Player Mentorship is fully remote — check-ins, film study, and accountability all happen virtually. Players anywhere in the country (or overseas) can join and develop under the same system.',
  },
  {
    q: 'What is included in Player Mentorship?',
    a: 'Bi-weekly one-on-one check-ins, film breakdowns, individualized skill and mindset goals, off-season and in-season planning, and direct access for questions between sessions.',
  },
  {
    q: 'How do mentorship check-ins and film study work?',
    a: 'Mentorship clients receive check-ins every two weeks to review their progress, goals, habits, and current challenges. Film study is used to evaluate decision-making, identify areas for improvement, and create clear action steps for continued development.',
  },
];

const BLUE = '#C9CDD4'; // silver accent (matches site theme)

const FaqItem = ({ item, number, isOpen, onToggle, testid }) => {
  return (
    <div
      data-testid={testid}
      className="border border-neutral-800/80 rounded-md overflow-hidden transition-colors duration-300"
      style={isOpen ? { borderColor: `${BLUE}66` } : undefined}
    >
      <button
        onClick={onToggle}
        data-testid={`${testid}-trigger`}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-neutral-900/40 transition-colors"
      >
        <span className="text-white text-[15px] leading-snug font-medium">
          <span className="mr-2 text-white/90">{number}.</span>
          {item.q}
        </span>
        {isOpen ? (
          <Minus className="w-5 h-5 flex-shrink-0" style={{ color: BLUE }} strokeWidth={2} />
        ) : (
          <Plus className="w-5 h-5 flex-shrink-0" style={{ color: BLUE }} strokeWidth={2} />
        )}
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 text-gray-300 text-[14px] leading-relaxed">
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CategoryHeader = ({ label }) => (
  <div className="flex items-center gap-4 mb-6">
    <h3
      className="font-display text-[15px] sm:text-base uppercase tracking-[0.35em] whitespace-nowrap"
      style={{ color: BLUE }}
    >
      {label}
    </h3>
    <div className="h-px flex-1" style={{ background: `linear-gradient(to right, ${BLUE}, transparent)` }} />
  </div>
);

export const BasketballFAQ = () => {
  const [openId, setOpenId] = useState('t-3'); // "How do I track..." open by default (matches mockup)

  const toggle = (id) => setOpenId((current) => (current === id ? null : id));

  return (
    <section
      data-testid="basketball-faq"
      className="relative bg-black py-16 sm:py-14 sm:py-16 lg:py-20 lg:py-24 px-6 border-t border-neutral-900 overflow-hidden"
    >
      {/* Subtle radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background: `radial-gradient(circle at 50% 0%, ${BLUE}14, transparent 60%)`,
        }}
      />

      <div className="relative container mx-auto max-w-7xl">
        {/* Eyebrow with side lines */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-center gap-4 mb-5"
        >
          <div className="h-px w-16 sm:w-32" style={{ background: `linear-gradient(to right, transparent, ${BLUE})` }} />
          <p
            className="font-display text-xs sm:text-sm uppercase tracking-[0.4em] whitespace-nowrap"
            style={{ color: BLUE }}
          >
            The Dillon Blueprint
          </p>
          <div className="h-px w-16 sm:w-32" style={{ background: `linear-gradient(to left, transparent, ${BLUE})` }} />
        </motion.div>

        {/* Main Title */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-center mb-4"
        >
          <h2
            data-testid="faq-heading"
            className="font-display text-white text-4xl sm:text-6xl lg:text-7xl uppercase tracking-tight leading-none"
            style={{ textShadow: '0 2px 24px rgba(255,255,255,0.04)' }}
          >
            Frequently Asked Questions
          </h2>
          <div className="mx-auto mt-5 h-1 w-48 sm:w-72 rounded-full" style={{ background: BLUE }} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="text-center text-gray-400 text-sm sm:text-base mb-14"
        >
          Everything you need to know about training and player mentorship.
        </motion.p>

        {/* Two-column FAQ grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Column 1: Training & Memberships */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="faq-column-training"
          >
            <CategoryHeader label="Training & Memberships" />
            <div className="space-y-3">
              {trainingFaqs.map((item, idx) => {
                const id = `t-${idx}`;
                return (
                  <FaqItem
                    key={id}
                    item={item}
                    number={idx + 1}
                    isOpen={openId === id}
                    onToggle={() => toggle(id)}
                    testid={`faq-item-training-${idx}`}
                  />
                );
              })}
            </div>
          </motion.div>

          {/* Column 2: Player Mentorship */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.25 }}
            data-testid="faq-column-mentorship"
          >
            <CategoryHeader label="Player Mentorship" />
            <div className="space-y-3">
              {mentorshipFaqs.map((item, idx) => {
                const id = `m-${idx}`;
                return (
                  <FaqItem
                    key={id}
                    item={item}
                    number={trainingFaqs.length + idx + 1}
                    isOpen={openId === id}
                    onToggle={() => toggle(id)}
                    testid={`faq-item-mentorship-${idx}`}
                  />
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Footer hint */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-14 flex flex-col items-center gap-3"
        >
          <div className="h-px w-24" style={{ background: BLUE }} />
          <p className="text-gray-500 text-sm italic">Select a question to view the answer.</p>
        </motion.div>
      </div>
    </section>
  );
};
